/**
 * Viewport file drop bridge (Faz 11.14 — B14 drag-open parity).
 *
 * Extends the Blocks-palette drop path (blockPlacement) with real file
 * drops: .yap/.bak/.sv$ load through the document pipeline, native CAD
 * (.dwg/.dxf) routes through the sniff-gated import (Faz 9). A drag with
 * files over the drafting canvas shows the drop indicator (inset accent
 * ring — no layout shift); files win over blocks when both are carried.
 */

import { create } from 'zustand';
import { openDroppedFile } from '../../utils/ProjectFileIO';
import { onBlockDrop, BLOCK_MIME } from './blockPlacement';

/** Drop indicator visibility (transient; the viewport shell reads it). */
interface FileDropState {
    dragging: boolean;
    setDragging: (value: boolean) => void;
}

export const useFileDropStore = create<FileDropState>((set) => ({
    dragging: false,
    setDragging: (dragging) => set({ dragging }),
}));

function hasFiles(e: React.DragEvent): boolean {
    const types = e.dataTransfer?.types;
    return !!types && Array.from(types).includes('Files');
}

function hasBlock(e: React.DragEvent): boolean {
    const types = e.dataTransfer?.types;
    return !!types && Array.from(types).includes(BLOCK_MIME);
}

/** Enter/leave counter so child elements don't flicker the indicator. */
let dragDepth = 0;

export function onViewportDragEnter(e: React.DragEvent): void {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth += 1;
    useFileDropStore.getState().setDragging(true);
}

export function onViewportDragOver(e: React.DragEvent): void {
    if (!hasFiles(e) && !hasBlock(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = hasFiles(e) ? 'copy' : e.dataTransfer.dropEffect;
}

export function onViewportDragLeave(e: React.DragEvent): void {
    if (!hasFiles(e)) return;
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) useFileDropStore.getState().setDragging(false);
}

/** Drop: files open through the sniff-gated pipeline; block drags fall
 *  through to the placement path unchanged. */
export function onViewportDrop(e: React.DragEvent): void {
    dragDepth = 0;
    useFileDropStore.getState().setDragging(false);
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        void openDroppedFile(files[0], files[0].name);
        return;
    }
    onBlockDrop(e);
}
